import { useCallback } from 'react';

import { useActor } from '@xstate/react';

import { useGlobalAppState } from '../../../../context/GlobalAppState';
import { useCloseBag } from '../../../../hooks';
import { useReorderNavigation } from '../useReorderNavigation';
import { useReorderTelemetry } from '../useReorderTelemetry';
import { getValidEventKeys, justStartedReorder, logger } from '../utils';

import type { ReorderEvent } from '../../machines/Reorder.machine';

export const useConfirmLocation = () => {
  const { reorderMachineRef } = useGlobalAppState();
  const [state, sendReorderEvent] = useActor(reorderMachineRef);
  const { startReorderTelemetry } = useReorderTelemetry();
  const { navigateToConfirmLocation, replaceWithConfirmLocation } = useReorderNavigation();
  const closeBag = useCloseBag();

  return useCallback(
    (event?: ReorderEvent, options?: { referrer?: string }) => {
      logger.info('Confirm Location', event);

      // ─── State Machine ──────────────────────────────────────────
      sendReorderEvent({
        ...getValidEventKeys(event),
        type: 'CONFIRM_LOCATION',
      });

      const navParams = options?.referrer ? { referrer: options.referrer } : undefined;

      // ─── Just Started Reorder ───────────────────────────────────
      if (justStartedReorder(state.value)) {
        // ─── Start Reorder Telemetry ──────────────────────────────
        startReorderTelemetry(event ?? state.context, 'confirm_location', event?.userInitiated);

        // ─── Navigation ───────────────────────────────────────────
        closeBag();
        setTimeout(() => navigateToConfirmLocation(navParams));

        return;
      }

      // ─── Continuing Reorder ─────────────────────────────────────
      replaceWithConfirmLocation(navParams);
    },
    [
      state.value,
      state.context,
      sendReorderEvent,
      closeBag,
      navigateToConfirmLocation,
      replaceWithConfirmLocation,
      startReorderTelemetry,
    ],
  );
};
